import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import logoWis from "../logo-wis.avif";
import Pagination from "../utils/Pagination";
import MiniDashboardItemCatalogue from "./MiniDashboardItemCatalogue";
import GlobalHeader from "./GlobalHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faPersonMilitaryPointing,
  faChevronDown,
  faChevronRight,
  faFilter, // Add this for the filter icon
  faTimes, // Add this for the "x" icon in filter tags
  faBuilding, // For customer filter
  faBarcode, // For item number filter
  faFileInvoice, // For HTS code filter
  faLanguage, // For description filter
  faRulerCombined, // For UMC filter
  faCubes, // For TLC/PS filter
  faIdCard, // For customer ID filter
  faEye,
  faHistory,
  faExchangeAlt,
  faSpinner,
  faQuestionCircle,
  faBell,
  faEnvelope,
  faUser,
  faSignOutAlt,
  faCog,
  faBoxes, // For Item Catalogue icon
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { saveAs } from "file-saver";
import logo from "../buckland.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getTranslation } from "../utils/i18n"; // Import the getTranslation function

const CustomerItemCatalogueGrid = () => {
  const navigate = useNavigate();
  const { logout, customerNumbers } = useAuth();
  const [language, setLanguage] = useState("en"); // State to manage the current language
  
  // State for menu dropdowns
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navigateToItemUsage = (customer_id, customer_rfc, item_number) => {
    navigate(
      `/customerItemCatalogueCrossedParts/${customer_id}/${customer_rfc}/${item_number}`
    );
  };

  const openInNewWindow = (path) => {
    window.open(path, "_blank");
  };

  const [allItems, setAllItems] = useState([]); // Holds all items from the backend
  const [items, setItems] = useState([]); // Items to display in the current page
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false); // State to manage filter visibility
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "" });
  const [filters, setFilters] = useState({
    item_description_spa: [],
    item_mx_hts_code: [],
    item_number: [],
    item_ps: [],
    item_tlc: [],
    item_umc: [],
    customer_name: [], // New filter for customer name
    customer_id: [], // New filter for customer ID
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetailsPanel, setShowItemDetailsPanel] = useState(false);

  const selectOptions = useMemo(() => {
    const fields = [
      "item_description_spa",
      "item_mx_hts_code",
      "item_number",
      "item_ps",
      "item_tlc",
      "item_umc",
      "customer_name",
      "customer_id",
    ];
    return fields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: [...new Set(allItems.map((item) => item[field]))].map(
          (value) => ({ value, label: value })
        ),
      }),
      {}
    );
  }, [allItems]);

  const [statistics, setStatistics] = useState({
    totalItemNumbers: 0,
    totalMxHts: 0,
    totalUmc: 0,
    totalUmf: 0,
    percentageTlc: 0.0,
    percentagePs: 0.0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filteredItems = allItems.filter((item) =>
      Object.entries(filters).every(
        ([key, selectedOptions]) =>
          selectedOptions.length === 0 ||
          selectedOptions.some((option) => option.value === item[key])
      )
    );

    setTotalPages(Math.ceil(filteredItems.length / pageSize));
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    setItems(filteredItems.slice(firstPageIndex, lastPageIndex));

    // Recalculate statistics based on the filtered items
    calculateStatistics(filteredItems);
  }, [allItems, currentPage, pageSize, filters]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters]);

  useEffect(() => {
    if (items.length) {
      let sortedItems = sortItems([...items]); // Use a separate function to sort items
      console.log("Sorted items: ", sortedItems); // Verify sorted items
      setItems(sortedItems);
    }
  }, [sortConfig]);

  const toggleFilters = () => {
    setFilterVisible(!filterVisible);
  };

  // Handler for selecting an item row
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowItemDetailsPanel(true);
  };

  const renderToggleIcon = () => (
    <FontAwesomeIcon
      icon={filterVisible ? faChevronDown : faChevronRight}
      onClick={toggleFilters}
      className="cursor-pointer text-gray-700 ml-2"
    />
  );

  const clearAllFilters = () => {
    setFilters({
      item_description_spa: [],
      item_mx_hts_code: [],
      item_number: [],
      item_ps: [],
      item_tlc: [],
      item_umc: [],
      customer_name: [],
      customer_id: [],
    });
  };

  // Custom action buttons for Item Catalogue
  const actionButtons = (
    <div className="flex items-center space-x-2">
      <select
        onChange={(e) => setLanguage(e.target.value)}
        value={language}
        className="bg-blue-600 hover:bg-blue-700 text-white border border-blue-500 rounded-md py-2 px-3 text-sm font-medium transition-colors duration-200"
      >
        <option value="en">English</option>
        <option value="es">Español</option>
      </select>
    </div>
  );
  const renderSelectFilters = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-4 border border-gray-200">
      <div className="flex justify-between items-center mb-3 border-b pb-2">
        <h2 className="text-sm font-medium text-gray-900">
          <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-500" />
          {getTranslation(language, "searchFilters")}
        </h2>
        <FontAwesomeIcon
          icon={filterVisible ? faChevronDown : faChevronRight}
          onClick={toggleFilters}
          className="cursor-pointer text-gray-700 hover:text-blue-600"
        />
      </div>

      {filterVisible && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "customerName")}
              </label>
              <Select
                isMulti
                name="customer_name"
                options={selectOptions["customer_name"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    customer_name: selectedOptions || [],
                  }))
                }
                value={filters["customer_name"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "customerName")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faIdCard}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "customerId")}
              </label>
              <Select
                isMulti
                name="customer_id"
                options={selectOptions["customer_id"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    customer_id: selectedOptions || [],
                  }))
                }
                value={filters["customer_id"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "customerId")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faBarcode}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "itemNumber")}
              </label>
              <Select
                isMulti
                name="item_number"
                options={selectOptions["item_number"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_number: selectedOptions || [],
                  }))
                }
                value={filters["item_number"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "itemNumber")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "mxHts")}
              </label>
              <Select
                isMulti
                name="item_mx_hts_code"
                options={selectOptions["item_mx_hts_code"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_mx_hts_code: selectedOptions || [],
                  }))
                }
                value={filters["item_mx_hts_code"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "mxHts")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faLanguage}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "descriptionSpa")}
              </label>
              <Select
                isMulti
                name="item_description_spa"
                options={selectOptions["item_description_spa"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_description_spa: selectedOptions || [],
                  }))
                }
                value={filters["item_description_spa"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "descriptionSpa")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faRulerCombined}
                  className="mr-1 text-blue-600"
                />
                {getTranslation(language, "umc")}
              </label>
              <Select
                isMulti
                name="item_umc"
                options={selectOptions["item_umc"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_umc: selectedOptions || [],
                  }))
                }
                value={filters["item_umc"]}
                placeholder={
                  getTranslation(language, "filterBy") +
                  " " +
                  getTranslation(language, "umc")
                }
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faCubes}
                  className="mr-1 text-blue-600"
                />
                TLC
              </label>
              <Select
                isMulti
                name="item_tlc"
                options={selectOptions["item_tlc"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_tlc: selectedOptions || [],
                  }))
                }
                value={filters["item_tlc"]}
                placeholder={getTranslation(language, "filterBy") + " TLC"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm text-gray-500 mb-1">
                <FontAwesomeIcon
                  icon={faCubes}
                  className="mr-1 text-blue-600"
                />
                PS
              </label>
              <Select
                isMulti
                name="item_ps"
                options={selectOptions["item_ps"]}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    item_ps: selectedOptions || [],
                  }))
                }
                value={filters["item_ps"]}
                placeholder={getTranslation(language, "filterBy") + " PS"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {Object.entries(filters).some(([key, value]) => value.length > 0) && (
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center flex-wrap">
                <span className="mr-2">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(filters).map(([key, selectedOptions]) =>
                    selectedOptions.map((item) => (
                      <span
                        key={`${key}-${item.value}`}
                        className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                      >
                        {key.split("_").join(" ")}: {item.label}
                        <button
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              [key]: prev[key].filter(
                                (i) => i.value !== item.value
                              ),
                            }))
                          }
                          className="ml-1 text-blue-500 hover:text-blue-700"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </span>
                    ))
                  )}

                  <button
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-800 underline text-xs ml-2"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const jsonToCSV = (json) => {
    const columns = Object.keys(json[0]);
    const header = columns.join(",");
    const rows = json.map((row) =>
      columns.map((fieldName) => JSON.stringify(row[fieldName])).join(",")
    );
    return [header, ...rows].join("\r\n");
  };

  const getAllFilteredItems = () => {
    return allItems.filter((item) =>
      Object.entries(filters).every(
        ([key, selectedOptions]) =>
          selectedOptions.length === 0 ||
          selectedOptions.some((option) => option.value === item[key])
      )
    );
  };

  // Function to handle CSV Download
  const handleDownloadCSV = () => {
    const filteredItems = getAllFilteredItems(); // Get all filtered items
    const csvData = jsonToCSV(filteredItems); // Convert them to CSV
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "exported-data.csv");
  };

  const keyMap = {
    [getTranslation(language, "customerName")]: "customer_name",
    [getTranslation(language, "customerId")]: "customer_id",
    [getTranslation(language, "rfc")]: "customer_rfc",
    [getTranslation(language, "itemNumber")]: "item_number",
    [getTranslation(language, "supplierItemNumber")]: "item_number_supplier",
    [getTranslation(language, "mxHts")]: "item_mx_hts_code",
    [getTranslation(language, "descriptionSpa")]: "item_description_spa",
    [getTranslation(language, "descriptionEng")]: "item_description_eng",
    [getTranslation(language, "usHts")]: "item_us_hts_code",
    [getTranslation(language, "umc")]: "item_umc",
    [getTranslation(language, "umf")]: "item_umf",
    [getTranslation(language, "unitPrice")]: "item_unitprice",
    [getTranslation(language, "tlc")]: "item_tlc",
    [getTranslation(language, "ps")]: "item_ps",
    [getTranslation(language, "satClave")]: "item_sat_claveproducto",
    [getTranslation(language, "satUm")]: "item_sat_um",
    [getTranslation(language, "embalaje")]: "item_sat_embalaje",
    [getTranslation(language, "matPeligroso")]: "item_sat_matpeligroso",
    [getTranslation(language, "umPeso")]: "item_sat_umpeso",
    [getTranslation(language, "createdAt")]: "date_created",
    [getTranslation(language, "updatedAt")]: "date_updated",
  };

  const sortItems = (itemsToSort) => {
    console.log(
      "Sorting by key:",
      sortConfig.key,
      "Direction:",
      sortConfig.direction
    );
    console.log("Sort Key:", sortConfig.key);

    const newItems = [...itemsToSort];
    return newItems.sort((a, b) => {
      console.log("Comparing", a[sortConfig.key], "with", b[sortConfig.key]);
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  // Function to render the item details side panel
  const renderItemDetailsPanel = () => {
    if (!selectedItem || !showItemDetailsPanel) return null;

    return (
      <div
        className="fixed inset-y-0 right-0 w-1/3 bg-white shadow-lg z-50 overflow-y-auto transform transition-transform duration-300 border-l border-gray-200"
      >
        {/* Panel Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-medium text-gray-900">
              {getTranslation(language, "itemDetails")}
            </h2>
            <button
              onClick={() => setShowItemDetailsPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Item Summary Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
              Item Summary
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Item Number:</span>
                <span className="font-medium">{selectedItem.item_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Description:</span>
                <span className="font-medium text-right max-w-[200px]">
                  {selectedItem.item_description_eng || selectedItem.item_description_spa}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Customer:</span>
                <span className="font-medium">{selectedItem.customer_name}</span>
              </div>
            </div>
          </div>

          {/* Item Information Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
              {getTranslation(language, "itemInformation")}
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(keyMap)
                .slice(0, 12)
                .map(([label, key]) => (
                  <div key={key}>
                    <span className="text-gray-500">{label}:</span>
                    <p className="font-medium">{selectedItem[key] || "N/A"}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={() =>
              openInNewWindow(
                `/customerItemCatalogueCrossedParts/${selectedItem.customer_id}/${selectedItem.customer_rfc}/${selectedItem.item_number}`
              )
            }
            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
          >
            <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
            {getTranslation(language, "viewCrossedPartsHistory")}
          </button>
        </div>
      </div>
    );
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else {
      direction = "ascending";
    }
    setSortConfig({ key, direction });
  };

  const fetchData = async (forceRefresh = true) => {
    setIsLoading(true);
    setError(null);
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      setError("Access token is not available");
      setIsLoading(false);
      return;
    }

    // Apply the customer number selection rules
    let customersToFetch = [];

    // Check if the user has any of the special customer numbers
    const hasSpecialCustomers = customerNumbers.some((num) =>
      ["xxx", "xxx", "xxx"].includes(num.toString().trim())
    );

    if (hasSpecialCustomers) {
      console.log("User has special customers, applying special rules");

      // If user has any special customer, always include 10614
      customersToFetch.push("10614");

      // Add any other customer numbers that aren't special
      customerNumbers.forEach((num) => {
        const trimmedNum = num.toString().trim();
        if (!["10612", "10615", "10614"].includes(trimmedNum)) {
          customersToFetch.push(trimmedNum);
        }
      });
    } else {
      // If no special customers, use all customer numbers as they are
      customersToFetch = customerNumbers.map((num) => num.toString().trim());
    }

    // Log the customers we're fetching for debugging
    console.log("Fetching data for customers:", customersToFetch);

    try {
      // Don't sort here - let the backend handle sorting consistently
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/customer_item_catalogue/getMasterDatabase`,
        {
          params: {
            customer_key: customersToFetch,
            refresh: forceRefresh,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      // You can display cache status to users if desired
      const cacheStatus = response.headers["x-cache"];
      const cacheTimestamp = response.headers["x-cache-timestamp"];
      const itemCount = response.headers["x-cache-items"];

      if (cacheStatus) {
        console.log(`Data source: ${cacheStatus}`);
        console.log(`Data timestamp: ${cacheTimestamp}`);
        console.log(`Item count: ${itemCount}`);

        // Optionally show a toast notification or update UI with cache info
      }

      setAllItems(response.data); // Store fetched data in allItems
      setTotalPages(Math.ceil(response.data.length / pageSize));

      // Calculate statistics
      calculateStatistics(response.data);
      setIsLoading(false);
    } catch (error) {
      setError("Failed to fetch data");
      setIsLoading(false);
      console.error("Failed to fetch data:", error);
    }
  };

  const renderPagination = () => (
    <Pagination
      total={totalPages * pageSize}
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={(page) => setCurrentPage(page)}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setCurrentPage(1);
      }}
    />
  );

  const calculateStatistics = (data) => {
    const itemNumbers = new Set(data.map((item) => item.item_number));
    const mxHts = new Set(data.map((item) => item.item_mx_hts_code));
    const umc = new Set(data.map((item) => item.item_umc));
    const umf = new Set(data.map((item) => item.item_umf));
    let tlcCount = data.filter((item) => item.item_tlc === "Y").length;
    let psCount = data.filter((item) => item.item_ps === "Y").length;

    setStatistics({
      totalItemNumbers: itemNumbers.size,
      totalMxHts: mxHts.size,
      totalUmc: umc.size,
      totalUmf: umf.size,
      percentageTlc: (tlcCount / data.length) * 100,
      percentagePs: (psCount / data.length) * 100,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-xl font-semibold animate-spin"
        />
        <div className="text-xl font-semibold ml-2">Loading...</div>
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <GlobalHeader 
        title={getTranslation(language, "customerItemCatalogue")}
        icon={faBoxes}
        actionButtons={actionButtons}
      />

      <div
        className="space-y-4 p-4 bg-gray-50"
        style={{ maxWidth: "100%" }}
      >
        <MiniDashboardItemCatalogue {...statistics} />
        {renderSelectFilters()}
        <div className="flex justify-end">
          <button
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
            onClick={handleDownloadCSV}
          >
            {getTranslation(language, "exportToCSV")}
          </button>
        </div>
        {renderPagination()}
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(keyMap).map((header, index) => {
                    const key = keyMap[header];
                    return (
                      <th
                        key={index}
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-blue-700"
                        onClick={() => requestSort(key)}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{header}</span>
                          {sortConfig.key === key && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "ascending" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getTranslation(language, "actions") || "Actions"}
                  </th>
                  <th scope="col" className="px-2 py-3"></th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {items.map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      selectedItem &&
                      selectedItem.item_number === item.item_number
                        ? "bg-blue-50"
                        : index % 2 === 0
                        ? "bg-white hover:bg-gray-50"
                        : "bg-gray-50 hover:bg-gray-100"
                    } cursor-pointer`}
                    onClick={() => handleItemSelect(item)}
                  >
                    {Object.entries(keyMap).map(([header, key], colIndex) => {
                      const cellValue = item[key] || "";
                      return (
                        <td
                          key={colIndex}
                          className="px-4 py-4 whitespace-nowrap text-sm text-gray-900"
                        >
                          {key === "item_mx_hts_code" ? (
                            <a
                              href={`/classificationDetails/${item.item_mx_hts_code}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {cellValue}
                            </a>
                          ) : (
                            cellValue
                          )}
                        </td>
                      );
                    })}
                    {/* Crossed Parts Button Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openInNewWindow(
                            `/customerItemCatalogueCrossedParts/${item.customer_id}/${item.customer_rfc}/${item.item_number}`
                          );
                        }}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                        title={getTranslation(language, "viewCrossedParts")}
                      >
                        <FontAwesomeIcon icon={faExchangeAlt} className="mr-1" />
                        {getTranslation(language, "crossedParts")}
                      </button>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openInNewWindow(
                            `/customerItemCatalogueCrossedParts/${item.customer_id}/${item.customer_rfc}/${item.item_number}`
                          );
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faPersonMilitaryPointing} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {renderItemDetailsPanel()}
    </>
  );
};

export default CustomerItemCatalogueGrid;
